/**
 * Category-bucket derivation for the Materials filter row + row icons.
 *
 * The design filters materials by category and shows a per-category icon on
 * each row (`docs/GardEn/handoff-2026-06/garden/screens-materials.jsx`
 * `MAT_CAT_FILTER` + `catIcon`, keyed on the fixture's `m.cat` slug). The
 * shipped quote API, however, serialises only an opaque `category_id` UUID — it
 * does NOT expose the category slug (`apps/api/garden/features/quotes/
 * schemas.py::MaterialItem`). So the client cannot map a material to a named
 * bucket (tile/paint/…) from the payload alone, and inventing a UUID→slug map
 * is forbidden (CLAUDE.md / GUARDRAILS).
 *
 * Resolution (documented demo decision — see the screen's cross-cutting note):
 * we discover the DISTINCT `category_id`s in payload appearance order and bind
 * each to the design's fixed category list in order. This guarantees the
 * important UX invariants — every row of one category shares an icon, and each
 * filter pill isolates exactly one real category — at the cost of the
 * label↔category pairing being best-effort for arbitrary data (it is exact for
 * the seeded Смета №142 when the category order matches). The recommended fix
 * is to add `category_slug` (or a localized category name) to `MaterialItem`.
 */
import type { IconName } from "@garden/ui";

import type { MaterialItem } from "@/lib/garden-api";

/** A non-"all" category bucket: the i18n label key + the row icon. */
export interface CategoryBucket {
  /** Key suffix under the `materials.filters.*` namespace. */
  labelKey: string;
  /** Row icon (design `catIcon` mapping). */
  icon: IconName;
}

/**
 * The design's fixed category order after "Все" (the `materials.filters`
 * tail), paired with its row icon. Index `i` here binds to the i-th distinct
 * `category_id` discovered in the materials list. `catIcon` (design):
 * tile→tile, paint→paint, electrics→electrics(bolt), rough→rough(layers),
 * doors→doors.
 */
export const CATEGORY_BUCKETS: readonly CategoryBucket[] = [
  { labelKey: "tile", icon: "tile" },
  { labelKey: "paint", icon: "paint" },
  { labelKey: "electrics", icon: "electrics" },
  { labelKey: "rough", icon: "rough" },
  { labelKey: "doors", icon: "doors" },
];

/** Fallback row icon when a material has no `category_id` (or more distinct
 * categories than the design's five buckets). */
export const FALLBACK_CATEGORY_ICON: IconName = "layers";

export interface CategoryBucketMap {
  /** Distinct `category_id`s in appearance order (slot → category_id). The
   * filter pill at index `i + 1` filters to `orderedCategoryIds[i]`. */
  orderedCategoryIds: readonly string[];
  /** category_id → its bucket (label + icon). */
  bucketByCategoryId: ReadonlyMap<string, CategoryBucket>;
}

/**
 * Build the appearance-ordered category map from a materials list. Materials
 * with a null `category_id` are skipped (they render with the fallback icon and
 * only appear under the "Все" filter).
 */
export function buildCategoryBuckets(
  materials: readonly MaterialItem[],
): CategoryBucketMap {
  const orderedCategoryIds: string[] = [];
  const bucketByCategoryId = new Map<string, CategoryBucket>();

  for (const material of materials) {
    const categoryId = material.category_id;
    if (categoryId === null) continue;
    if (bucketByCategoryId.has(categoryId)) continue;
    const bucket = CATEGORY_BUCKETS[orderedCategoryIds.length];
    orderedCategoryIds.push(categoryId);
    if (bucket !== undefined) {
      bucketByCategoryId.set(categoryId, bucket);
    }
  }

  return { orderedCategoryIds, bucketByCategoryId };
}

/** The row icon for a material: its bucket icon, or the fallback. */
export function iconForMaterial(
  material: MaterialItem,
  map: CategoryBucketMap,
): IconName {
  if (material.category_id === null) return FALLBACK_CATEGORY_ICON;
  return map.bucketByCategoryId.get(material.category_id)?.icon ?? FALLBACK_CATEGORY_ICON;
}
